import {
    Api, Currency, CurrencySettings, CurrencyUtil, DisplayType, DisplayValue, FieldUpdateEvent, LogManager, Model,
    ModelRow, Navigation, StringUtil
} from "@mcleod/core";
import { AutogenLayoutPendingPayAdjustment } from "./autogen/AutogenLayoutPendingPayAdjustment";
import {
    ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DropdownSelectionEvent, EditRowDecorator,
    Layout, LookupModelSearchEvent, LookupModelSelectionEvent, Snackbar, Textbox, ValidationResult,
    DataSourceModeChangeEvent
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { READY_TO_PAY_ITEMS, Settlement } from "./Settlement";
import { TriumphPayTxDetail } from "./TriumphPayTxDetail";
import { ImageName } from "@mcleod/images";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { TriumphNotifications } from "./TriumphNotifications";

const log = LogManager.getLogger("lme.settlement.PendingPayAdjustment");

export class PendingPayAdjustment extends AutogenLayoutPendingPayAdjustment {
    private payrollControl: ModelRow;
    private triumphpayFieldsUpdated: boolean = false;
    private triumphPayNeedsCancellation: boolean = false;

    override async onLoad(): Promise<void> {
        CurrencyUtil.configureCurrencySelector(this.textboxCurrency);
        this.addLayoutDataLoadListener(() => {
            this.applyAdjustAccountAttribs();
        });
        this.payrollControl = await Model.searchSingleRecord("lme/dispatch/payroll-control");

        this.panelSourceCurrency.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();

        this.sourceDrsPendingDeduct.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() == DataSourceAction.SEARCH) {
                this.textboxOrderId.quickInfoOverrideMethod = async () => {
                    return Layout.getLoadedLayoutWithData("lme/settlement/OrderPayablesOrderIdQuickInfo", null, this.activeRow.get("movement_id"));
                }
                Settlement.preventUpdateDuringProcessing(this, "Pending Pay Adjustment");
            }
            const payeeID = this.activeRow?.get("payee_id");
            if (!StringUtil.isEmptyString(payeeID)) {
                TriumphPayTxDetail.isPayeeTriumphPay(payeeID).then(isTriumphPay => this.tabTriumphPay.visible = isTriumphPay);
            }
            this.setTitle();
        });
        this.textboxCurrency.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.changeCurrencyCode();
            }
        });
        this.textboxPayeeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                const selection = event.selection;
                if (this.mainDataSource.mode === DataSourceMode.ADD && CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                    this.activeRow.setValueAndLookupModelData("currency", selection.get("currency"), selection.getLookupModelData("currency"));
                    this.changeCurrencyCode();
                }
                const payeeAccepted = !selection.isNull("drs_payee.process_status") && (selection.get("drs_payee.type_of") === "O" || selection.get("drs_payee.type_of") === "C");
                if (payeeAccepted) {
                    this.textboxPayeeId.imagePreName = ImageName.circleCheck;
                    this.textboxStatus.removeItemsWithValue("Y");
                    this.textboxStatus.setSelectedItemFromValue("N");
                } else if (!payeeAccepted && this.textboxStatus.resolveItems().find(item => item.value == "Y") == null) {
                    this.textboxPayeeId.imagePreName = null;
                    this.textboxStatus.items = READY_TO_PAY_ITEMS;
                }
            }
        });
        this.textboxTractorId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.textboxTractorId.validateSimple();
            }
        });
        this.textboxTractorId.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            const result: ValidationResult = {
                component: this.textboxTractorId,
                isValid: true
            };
            const lookupModelData = this.textboxTractorId.getFirstLookupModelData();
            if (mode === DataSourceMode.SEARCH || !lookupModelData || !this.activeRow.isNull("order_id") || !this.activeRow.isNull("movement_id")) {
                return result;
            }
            if (lookupModelData.get("pay_owner") === "O" || lookupModelData.get("pay_owner") === "B") {
                if (lookupModelData.get("owner") !== this.activeRow.get("payee_id")) {
                    result.isValid = false;
                    result.validationMessage = "Payee is not the owner of the tractor.";
                }
            }
            return result;
        };
        this.layoutTriumphPayTxDetail.hideNonDrsDeductFields();
    }

    private async changeCurrencyCode() {
        const newCurrencyCode = this.textboxCurrency.text;

        const [amount] = [this.activeRow.getCurrency("amount")];

        if (amount) {
            amount.setCurrencyCode(newCurrencyCode);
            this.activeRow.set("amount", amount);
        }
    }

    sourceDrsPendingDeductAfterModeChange(event: DataSourceModeChangeEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            this.textboxOrderId.lookupModelDisabled = true;
            this.textboxMovementId.lookupModelDisabled = true;
            TriumphPayTxDetail.isTriumphPayActive().then(isActive => this.tabTriumphPay.visible = isActive);
        }
        if (this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE) {
            if (!this.activeRow.isNull("drs_payee.process_status") && this.activeRow.get("ready_to_pay_flag") !== "Y") {
                this.textboxStatus.removeItemsWithValue("Y");
            }
            this.textboxBackOfficeReasonCode.enabled = this.activeRow.get("ready_to_pay_flag") == "N";
            this.textboxReasonCodeComment.enabled = this.activeRow.get("ready_to_pay_flag") == "N";
            this.activeRow.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
                if (event.fieldName === "units" || event.fieldName === "rate") {
                    const units = this.activeRow.getNumber("units", 0);
                    const rate = this.activeRow.getNumber("rate", 0);
                    const amount = new Currency(units * rate);
                    if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                        amount.setCurrencyCode(this.textboxCurrency.text);
                    }
                    this.activeRow.set("amount", amount);
                    this.applyAdjustAccountAttribs();
                }
                this.setButtonCreateReimbursementEnabled();
                this.setSplitAdjustmentButtonEnabled();
            });
            if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
                this.buttonCreateReimbursement.visible = true;
                if (this.textboxCurrency.visible && this.textboxCurrency.enabled) {
                    this.textboxCurrency.enabled = this.activeRow.isNull("gl_accrual_amt") || this.activeRow.getCurrency("gl_accrual_amt").isZero();
                    if (!this.textboxCurrency.enabled) {
                        this.textboxCurrency.disabledTooltip = "Currency cannot be updated when journal entries have already been created";
                    }
                }
                this.buttonSplitAdjustment.visible = true;
                CurrencyUtil.configureCurrencySelector(this.textboxCurrency);
            }
        }
    }

    setSplitAdjustmentButtonEnabled() {
        const assetPayeeAccepted = (this.activeRow.get("drs_payee.type_of") === "O" || this.activeRow.get("drs_payee.type_of") === "C") && !this.activeRow.isNull("drs_payee.process_status");
        this.buttonSplitAdjustment.enabled = (this.activeRow.get("ready_to_pay_flag") === "N" || (!assetPayeeAccepted && this.activeRow.isNull("process_status") && this.activeRow.get("ready_to_pay_flag") !== "V")) && !this.activeRow.hasChanged();
        if (this.activeRow.get("ready_to_pay_flag") === "V") {
            this.buttonSplitAdjustment.disabledTooltip = "Pay Adjustment record has already been voided.";
        } else if (this.activeRow.get("process_status") === "C") {
            this.buttonSplitAdjustment.disabledTooltip = "The record has been included on a payment. You cannot change the Pay Adjustment.";
        } else if (this.activeRow.get("process_status") === "S") {
            this.buttonSplitAdjustment.disabledTooltip = "The record has been included on a summary. You cannot change the Pay Adjustment.";
        } else {
            this.buttonSplitAdjustment.disabledTooltip = null;
        }
    }

    setButtonCreateReimbursementEnabled() {
        const fieldsChanged: boolean = this.activeRow.hasChanged();
        const payeeAccepted: boolean = this.activeRow.get("drs_payee.process_status") === "A";
        const payeeSummaryPrinted: boolean = this.activeRow.get("drs_payee.process_status") === "S";
        const payeeCheckPrinted: boolean = this.activeRow.get("drs_payee.process_status") === "C";
        this.buttonCreateReimbursement.enabled = !(fieldsChanged || payeeAccepted || payeeSummaryPrinted || payeeCheckPrinted);
        if (payeeAccepted || payeeSummaryPrinted || payeeCheckPrinted) {
            this.buttonCreateReimbursement.disabledTooltip = "Pending Pay Adjustment is being processed in Driver Settlement Processing. Pending Pay Adjustment not available for update.";
        } else {
            this.buttonCreateReimbursement.disabledTooltip = null;
        }
    }

    async applyAdjustAccountAttribs() {
        const row = this.mainDataSource.activeRow;
        if (PendingPayAdjustment.requiresAdjustmentAccount(row)) {
            this.textboxAdjustmentGlid.enabledDuringUpdate = true;
            this.textboxAdjustmentGlid.required = true;
            const adjustmentId = this.payrollControl.get("adjustment_glid");
            if (this.textboxAdjustmentGlid.isEmpty() && !StringUtil.isEmptyString(adjustmentId)) {
                this.setGlidLookup(this.textboxAdjustmentGlid, adjustmentId);

            }
        } else {
            this.textboxAdjustmentGlid.clear();
            this.textboxAdjustmentGlid.required = false;
            this.textboxAdjustmentGlid.enabledDuringUpdate = false;
        }
    }

    static requiresAdjustmentAccount(row: ModelRow) {
        const postModule = row.getString("post_module", "");
        const readyToPay = row.getString("ready_to_pay_flag", "");
        const amount = row.getCurrency("amount");
        const accrualAmount = row.getCurrency("gl_accrual_amt");
        return PendingPayAdjustment.fromAPFUELWIRE(postModule)
            && (accrualAmount != null && !accrualAmount.isZero())
            && (readyToPay == "V" || amount.getAmount() != accrualAmount.getAmount());
    }

    static fromAPFUELWIRE(postModule: string) {
        const modules = ["AP", "FUEL", "WIRE"];
        return modules.includes(postModule);
    }

    protected setTitle() {
        if (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return;
        }
        const orderId = this.mainDataSource.activeRow?.get("order_id");
        const payeeData = this.mainDataSource.activeRow?.getLookupModelData("payee_id");
        const payee = payeeData && payeeData[0] ? " - " + payeeData[0].get("name") : "";
        const titleSuffix = StringUtil.isEmptyString(orderId) ? payee : " - " + orderId + payee;
        this.dataHeader.title = this.titleSingular + titleSuffix;
    }

    setGlidLookup(textbox: Textbox, id: string) {
        const modelRow = this.mainDataSource.activeRow;
        modelRow.set(textbox.field, id);
        const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
        lookupLayout.addLayoutLoadListener(() => {
            const filter = { search: id };
            lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                if (response?.modelRows?.length === 1) {
                    modelRow.setLookupModelData(textbox.field, response.modelRows[0]);
                } else {
                    const lookupModelData = new ModelRow(modelRow._modelPath, false);
                    lookupModelData.set(textbox.lookupModelResultField, modelRow.get(textbox.field));
                    lookupModelData.set(textbox.lookupModelDisplayField, modelRow.get(textbox.field));
                    modelRow.setLookupModelData(textbox.field, lookupModelData);
                }
                textbox.displayData(modelRow, null, 0);
            });
        });
    }


    /** This is an event handler for the onLookupModelSelection event of textboxDeductCodeId. */
    textboxDeductCodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH && event.hasSelection() === true) {
            if (this.activeRow.originalData != null && !this.validDeductCodeChange(this.activeRow.originalData["deduction_type"], event.selection.get("code_type"))) {
                this.mainDataSource.activeRow.revertToOriginalData("deduct_code_id");
                return;
            }
            this.activeRow.set("short_desc", event.selection.get("short_desc"));
            this.activeRow.set("deduction_type", event.selection.get("code_type"));
            if (!event.selection.isNull("amount")) {
                this.textboxUnits.text = "1.0";
                this.mainDataSource.activeRow.set("rate", event.selection.getCurrency("amount").getAmount());
            }
            if (!event.selection.isNull("glid")) {
                this.setGlidLookup(this.textboxGlid, event.selection.get("glid"));
            }
            this.applyAdjustAccountAttribs();

        }

    }

    validDeductCodeChange(oldType: string, newType: string): boolean {
        if (this.isEarning(oldType) && this.isDeduction(newType) && !this.activeRow.isNull("gl_accrual_amt")) {
            Snackbar.showWarningSnackbar("Accrued pay adjustment can not be changed from a "
                + "reimbursement / earning to a deduction. Please void this entry and re-add to correct", { targetPanel: this });
            return false;
        }
        if (this.isEarning(newType) && this.isDeduction(oldType) && !this.activeRow.isNull("gl_accrual_amt")) {
            Snackbar.showWarningSnackbar("Accrued pay adjustment can not be changed from a "
                + "deduction to a reimbursement / earning. Please void this entry and re-add to correct.", { targetPanel: this });
            return false;
        }
        return true;
    }

    isEarning(type: string): boolean {
        return type == "E" || type == "R";
    }

    isDeduction(type: string): boolean {
        return type == "D" || type == "B";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxGlid. */
    textboxGlidBeforeLookupModelSearch(event) {
        event.filter.isactive = "Y";
    }

    /** This is an event handler for the afterDropdownSelection event of textboxStatus. */
    textboxStatusAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.applyAdjustAccountAttribs();
        this.textboxBackOfficeReasonCode.enabled = this.activeRow.get("ready_to_pay_flag") == "N";
        this.textboxReasonCodeComment.enabled = this.activeRow.get("ready_to_pay_flag") == "N";
        if (this.activeRow.get("ready_to_pay_flag") != "N") {
            this.textboxBackOfficeReasonCode.clear();
            this.textboxReasonCodeComment.clear();
        }
        TriumphPayTxDetail.isTriumphPayActive().then(active => {
            if (active && this.needsCancellation()) {
                Snackbar.showWarningSnackbar("Voiding this solo pay adjustment will cancel the submitted TriumphPay payment(s).");
            }
        });
    }

    textboxDeductCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    textboxOrderIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;

            // Movement Fields
            this.activeRow.setValueAndLookupModelData("movement_id", selection.get("id"), { id: selection.get("id") });
            CommonTooltips.setTooltipContentLayout(this.textboxOrderId, this.activeRow.get("movement_id"), "lme/settlement/OrderPayablesOrderIdQuickInfo");
        } else if (event.hasDeselection()) {
            this.activeRow.setValueAndLookupModelData("movement_id", null, null);
        }
    }

    textboxBackOfficeReasonCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
        event.filter.code_type = "S";
    }

    textboxOrderIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.textboxOrderId.text?.length < this.textboxOrderId.lookupModelMinChars) {
            event.preventDefault();
        } else {
            event.filter.order_id = this.textboxOrderId.text;
            event.filter.lm_search = this.textboxOrderId.text;
        }
    }

    textboxMovementIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.textboxOrderId.getFirstLookupModelData() == null) {
            event.preventDefault();
        } else {
            event.filter.order_id = this.textboxOrderId.text;
            event.filter.lm_search = this.textboxOrderId.text;
        }
    }

    async sourceDrsPendingDeductBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.UPDATE) {
            if (StringUtil.equalsIgnoreCase(this.activeRow.get("payment_method"), "U")) {
                const hasPayAdjustmentAmountChanged = this.activeRow.getCurrency("amount")?.amount !== this.sourceDrsPendingDeduct.activeRow.originalData?.["amount"]?.amount;
                const hasBeenVoided = this.activeRow.get("ready_to_pay_flag") === "V"
                this.triumphpayFieldsUpdated = hasPayAdjustmentAmountChanged || hasBeenVoided
                this.triumphPayNeedsCancellation = this.needsCancellation();
            }
        }
    }

    private needsCancellation() {
        const hasBeenVoided = this.activeRow.get("ready_to_pay_flag") === "V"
        const noSettlementPay = !(this.activeRow.getCurrency("settlement_total_pay", new Currency(0)).isGreaterThan(new Currency(0)));
        const noAdjustmentPay = !(this.activeRow.getCurrency("adjustment_other_pay", new Currency(0)).isGreaterThan(new Currency(0)));
        return hasBeenVoided && noSettlementPay && noAdjustmentPay && !this.activeRow.isNull("submitted_settlement_id");
    }

    async sourceDrsPendingDeductAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.UPDATE){
            if (this.triumphPayNeedsCancellation) {
                // can treat this as successful because posting fails in row validate method on backend if cancel fails
                this.clearTriumphPayCancellationFields();
                TriumphNotifications.showCancellationSuccess(
                    this.activeRow.get("order_id"),
                    this.activeRow.get("payee_name")
                );
            }
            else if (this.triumphpayFieldsUpdated) {
                try {
                    const triumphPayResponse = await Api.post("lme/triumphpay/updates", {
                        order_id: this.activeRow.get("order_id"),
                        payee_id: this.activeRow.get("payee_id"),
                        movement_id: this.activeRow.get("movement_id"),
                        process_status: this.activeRow.get("process_status")
                    });

                    const responseData = triumphPayResponse?.data[0];

                    if (responseData?.success) {
                        TriumphNotifications.showUpdateSuccess(this.activeRow.get("order_id"));
                    } else {
                        TriumphNotifications.showErrorSnackbar(
                            responseData?.error,
                            responseData?.is_update ?? false,
                            this
                        );
                    }
                } catch (error) {
                    const errorMessage = `TriumphPay Update Error: ${error.message || "Unknown error"}`;
                    Snackbar.showWarningSnackbar(errorMessage, { targetPanel: this });
                }
            }
        }
        this.setButtonCreateReimbursementEnabled();
        this.setSplitAdjustmentButtonEnabled();
    }

    private clearTriumphPayCancellationFields() {
        this.activeRow.set("triumphpay_pymt_id", null);
        this.activeRow.set("triumphpay_pymt_status", null);
        this.activeRow.set("triumphpay_transaction_date", null);
        this.activeRow.set("triumphpay_transaction_num", null);
        this.activeRow.set("triumphpay_carrierpay_date", null);
        this.activeRow.set("triumphpay_confirm_code", null);
        this.activeRow.set("triumphpay_pymt_details", null);
        this.activeRow.set("process_status", null);
        this.activeRow.set("triumphpay_quickpay", false);
        this.activeRow.set("triumphpay_process_date", new Date());
    }

    textboxPayeeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && this.mainDataSource.mode === DataSourceMode.ADD) {
            const selection = event.selection;
            const payeePaymentMethod = selection.get("payment_method");
            if (StringUtil.equalsIgnoreCase(payeePaymentMethod, "U")) {
                this.textboxStatus.selectedItem = this.textboxStatus.items[1];
                this.textboxStatus.enabled = false;
            } else {
                this.textboxStatus.selectedItem = this.textboxStatus.items[0];
                this.textboxStatus.enabled = true;
            }
        }
    }

    async buttonCreateReimbursementOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/settlement/PendingPayAdjustment") as PendingPayAdjustment;
        const data = await this.mainDataSource.createBlankRow();
        const payeeLookupModelData = this.activeRow.getLookupModelData("payee_id");
        data.setValueAndLookupModelData("payee_id", this.activeRow.get("payee_id"), payeeLookupModelData);
        data.set("transaction_date", this.activeRow.get("transaction_date"));
        data.setValueAndLookupModelData("movement_id", this.activeRow.get("movement_id"), { id: this.activeRow.get("movement_id") });
        data.setValueAndLookupModelData("order_id", this.activeRow.get("order_id"), { "movement_order.order_id": this.activeRow.get("order_id") });
        data.set("ready_to_pay_flag", this.activeRow.get("ready_to_pay_flag"));
        data.set("units", this.activeRow.get("units"));
        data.set("rate", this.activeRow.get("rate"));
        data.set("amount", this.activeRow.getCurrency("amount"));
        data.set("reference_no", this.activeRow.get("reference_no"));
        const tractorLookupModelData = this.activeRow.getLookupModelData("tractor_id");
        if (tractorLookupModelData) {
            data.setValueAndLookupModelData("tractor_id", this.activeRow.get("tractor_id"), tractorLookupModelData);
        }
        const driverLookupModelData = this.activeRow.getLookupModelData("driver_id");
        if (driverLookupModelData) {
            data.setValueAndLookupModelData("driver_id", this.activeRow.get("driver_id"), driverLookupModelData);
        }
        layout.mainDataSource.addAfterModeChangeListener(() => {
            data.set("drs_payee.process_status", this.activeRow.get("drs_payee.process_status"));
            const isCompanyDriverOrOwnerOperator: boolean = this.activeRow.get("drs_payee.type_of") === "C" || this.activeRow.get("drs_payee.type_of") === "O";
            if (!this.activeRow.isNull("drs_payee.process_status") && isCompanyDriverOrOwnerOperator) {
                layout.textboxStatus.removeItemsWithValue("Y");
                layout.textboxStatus.setSelectedItemFromValue("N");
            }
            layout.textboxDeductionType.setSelectedItemFromValue("R");
        });
        new EditRowDecorator({
            layout: layout,
            dataSourceMode: DataSourceMode.ADD,
            data: data,
            width: "75%",
            title: "Create Escrow Reimbursement",
            onValidate: (updatedData: ModelRow): ValidationResult[] => {
                const validationResults: ValidationResult[] = layout.mainDataSource.componentValidator.validate();
                if (validationResults && validationResults.length > 0) {
                    ValidationUtil.showFailedValidationDialog(validationResults, "Unable to save this record because data is missing or invalid.");
                }
                return validationResults;
            },
            layoutLoadListeners: () => {
                layout.textboxDeductCodeId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                    event.filter.code_type = "R";
                    event.filter.is_active = "Y";
                });
                layout.textboxGlid.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                    event.filter.isactive = "Y";
                    event.filter.is_escrow = "Y";
                });
                layout.textboxGlid.addlValidationCallback = () => {
                    const result: ValidationResult = {
                        component: layout.textboxGlid,
                        isValid: true
                    };
                    const lookupModelData = layout.textboxGlid.getFirstLookupModelData();
                    if (lookupModelData?.getBoolean("is_escrow", false) !== true) {
                        result.isValid = false;
                        result.validationMessage = "GL account must be an escrow account.";
                        result.caption = "GL account must be an escrow account.";
                    }
                    return result;
                }
            },
            onSave: (row: ModelRow) => {
                layout.mainDataSource.post().then(() => {
                    const url = this.layoutName + "?mode=update&key=" + row.get("id");
                    Navigation.navigateTo(url, { newTab: true });
                });
            }
        });
    }

    async buttonSplitAdjustmentOnClick(event: ClickEvent) {
        this.buttonSplitAdjustment.busy = true;
        if (this.validateSimple()) {
            const inputTextbox = new Textbox({
                captionVisible: true,
                fillRow: true,
                required: true,
                caption: "Amount to Split Off",
                displayType: DisplayType.CURRENCY
            });
            inputTextbox.addlValidationCallback = (value) => {
                const result: ValidationResult = {
                    isValid: true,
                    component: inputTextbox
                };

                // Get the split amount and original amount for validation
                const displayValue = DisplayValue.getCurrencyDisplayValue(value);
                const splitAmountCurrency = CurrencyUtil.getCurrencyObjectFromDataValue(displayValue);
                const originalAmount = this.activeRow.getCurrency("amount");

                // Validate split amount
                if (!splitAmountCurrency || !splitAmountCurrency.amount || splitAmountCurrency.amount <= 0) {
                    result.isValid = false;
                    result.validationMessage = "Split amount must be greater than zero";
                    return result;
                }

                if (!originalAmount || !originalAmount.amount) {
                    result.isValid = false;
                    result.validationMessage = "Original pay adjustment has no amount";
                    return result;
                }

                if (splitAmountCurrency.amount >= originalAmount.amount) {
                    result.isValid = false;
                    result.validationMessage = "Split amount must be less than the original amount";
                    return result;
                }

                return result;
            };
            const dialogResponse = await CommonDialogs.showDialog(inputTextbox, {
                title: "Split Pay Adjustment",
                okDefault: true
            });
            if (!inputTextbox.isEmpty() && !dialogResponse.wasCancelled) {
                const splitAmountCurrency = CurrencyUtil.getCurrencyObjectFromDataValue(inputTextbox.getDataValue());

                try {
                    const response = await Api.post("lme/settlement/split-pay-adjustment", {
                        pay_adjustment_id: this.activeRow.get("id"),
                        split_amount: splitAmountCurrency
                    });
                    const data = response?.data?.[0];
                    if (data) {
                        this.mainDataSource.search(this.mainDataSource.lastSearch);
                        Navigation.navigateTo("lme/settlement/PendingPayAdjustment?mode=update&key=" + data.new_pay_adjustment_id, { newTab: true });
                    }
                } catch (error) {
                    await CommonDialogs.showError(error);
                }
            }
        }
        this.buttonSplitAdjustment.busy = false;
    }
}
